import React, { useEffect, useMemo, useState } from "react";
import Modal from "../comman/modal.js";
import Button from "../comman/button.js";
import LoadingSpinner from "../LoadingSpinner/loadingSpinner.js";
import { getOrderById, getExternalOrder } from "../../services/order-service.js";
import { fetchAdjustableSpiceLevels } from "../../services/menu-service.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation, getLocalizedValue } from "../../utils/translate.js";
import { useDarkMode } from "../../utils/darkModeContext.js";
import { getOrderStatusStyles } from "../../utils/orderStatusUtils.js";
import { formatPriceWithCurrency } from "../../utils/formatUtils.js";
import { formatDate, formatTime } from "../../utils/dateUtils.js";
import { DISH_IMAGE_PLACEHOLDER_40 } from "../../utils/dishImagePlaceholder.js";
import { resolveOrderCustomer } from "../../utils/orderCustomer.js";

/** Same external check the board uses to route to the integration-order page. */
export const externalOrderKey = (order) => {
    if (order?.id != null) {
        return null;
    }
    const src = order?.source;
    const key = String(order?.orderNumber ?? order?.number ?? "").trim();
    return src != null && src !== "BYCHEF" && key !== "" ? { source: String(src), key } : null;
};

/**
 * Quick-look modal for a board card: dishes with images, additions, note and totals.
 * Renders the board's summary data immediately, then swaps in the full order once loaded.
 */
const BoardOrderDetailsModal = ({ order, onClose, onOpenFull, toastRef }) => {
    const { language } = useLanguage();
    const { isDarkMode } = useDarkMode();
    const [details, setDetails] = useState(null);
    const [spiceLevels, setSpiceLevels] = useState([]);
    const [loading, setLoading] = useState(true);

    const external = externalOrderKey(order);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const full = external
                    ? await getExternalOrder(external.key, external.source)
                    : await getOrderById(order.id);
                if (!cancelled && full) setDetails(full);
            } catch (err) {
                console.error("Failed to load order details for board modal:", err);
                if (!cancelled) {
                    toastRef?.current?.addToast(getTranslation("common.error", language), "error");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        fetchAdjustableSpiceLevels()
            .then((levels) => {
                if (!cancelled && levels) setSpiceLevels(levels);
            })
            .catch((e) => console.error("Error fetching spice levels:", e));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.id]);

    const info = details || order || {};
    const dishes = Array.isArray(info.orderDishList) ? info.orderDishList : [];
    const customerName = resolveOrderCustomer(info).fullName;

    const discountAmount = useMemo(() => {
        const before = parseFloat(info.totalPriceBeforeDiscount);
        const after = parseFloat(info.totalPrice);
        if (isNaN(before) || isNaN(after) || before <= after) return null;
        return before - after;
    }, [info.totalPriceBeforeDiscount, info.totalPrice]);

    const statusStyle = info.status ? getOrderStatusStyles({ value: info.status }, isDarkMode) : null;
    const promoCode = info.promoCode || info.promoCodeUsages?.promoCode || info.promoCodeUsages?.code;

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={`${getTranslation("orderContentPage.order", language)} #${info.number ?? order?.number ?? ""}`}
            size="lg"
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                    {statusStyle && (
                        <span
                            className="rounded px-2 py-0.5 text-xs font-bold"
                            style={{ backgroundColor: statusStyle.backgroundColor, color: statusStyle.color }}
                        >
                            {getTranslation(`orderStatuses.${String(info.status).toLowerCase()}`, language)}
                        </span>
                    )}
                    <span className="font-semibold tabular-nums text-gray-700 dark:text-[#c9d1d9]">
                        {formatDate(info.deliveryDateTime)} {formatTime(info.deliveryDateTime)}
                    </span>
                    {info.asSoonAsPossible && (
                        <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-semibold text-gray-800 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                            {getTranslation("orderContentPage.asapShort", language)}
                        </span>
                    )}
                    {loading && <LoadingSpinner size="small" className="!justify-start" />}
                </div>

                {(customerName || info.receiverPhoneNumber) && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        {customerName && (
                            <span className="font-semibold text-gray-900 dark:text-[#e6edf3]">{customerName}</span>
                        )}
                        {info.receiverPhoneNumber && (
                            <a
                                href={`tel:${info.receiverPhoneNumber}`}
                                className="font-medium text-customOrange hover:underline"
                            >
                                {info.receiverPhoneNumber}
                            </a>
                        )}
                    </div>
                )}

                <div>
                    <h3 className="mb-2 text-sm font-bold text-gray-900 dark:text-[#e6edf3]">
                        {getTranslation("orderContentPage.orderedDishes", language)}
                    </h3>
                    {dishes.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-[#8b949e]">
                            {getTranslation("common.noDataFound", language)}
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-[#21262d]">
                            {dishes.map((dish, index) => (
                                <li key={dish?.id ?? index} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                                    <img
                                        src={dish?.dishDto?.url || DISH_IMAGE_PLACEHOLDER_40}
                                        alt={
                                            getLocalizedValue(dish?.dishDto, "name", language) ||
                                            getTranslation("common.dish", language)
                                        }
                                        className="h-12 w-12 shrink-0 rounded-lg border object-cover dark:border-[#30363d]"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = DISH_IMAGE_PLACEHOLDER_40;
                                        }}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="min-w-0 break-words text-sm font-medium text-gray-900 dark:text-[#e6edf3]">
                                                <span className="mr-1 font-bold tabular-nums">{dish?.quantity ?? 1}×</span>
                                                {getLocalizedValue(dish?.dishDto, "name", language) || "—"}
                                            </p>
                                            {dish?.price ? (
                                                <span className="shrink-0 text-sm font-medium tabular-nums text-gray-700 dark:text-[#b1bac4]">
                                                    {formatPriceWithCurrency(dish.price)}
                                                </span>
                                            ) : null}
                                        </div>
                                        {dish?.selectedSpiceLevel && (
                                            <p className="mt-0.5 text-xs text-gray-600 dark:text-[#8b949e]">
                                                {getTranslation("common.spiceLevel", language)}:{" "}
                                                {spiceLevels.find((s) => s.id === dish.selectedSpiceLevel)?.spiceLevel || ""}
                                            </p>
                                        )}
                                        {dish?.orderDishAdditionDtoList?.length > 0 && (
                                            <ul className="mt-1 space-y-0.5">
                                                {dish.orderDishAdditionDtoList.map((item, idx) => (
                                                    <li
                                                        key={item?.id ?? idx}
                                                        className="flex justify-between gap-2 text-xs text-gray-600 dark:text-[#8b949e]"
                                                    >
                                                        <span className="min-w-0 break-words">
                                                            <span className="font-medium">
                                                                {getLocalizedValue(item?.dishAdditionDto?.group, "name", language)}
                                                            </span>
                                                            {getLocalizedValue(item?.dishAdditionDto?.group, "name", language) ? ": " : ""}
                                                            {getLocalizedValue(item?.dishAdditionDto, "name", language)}
                                                        </span>
                                                        {item?.price ? (
                                                            <span className="shrink-0 font-medium tabular-nums text-gray-700 dark:text-[#b1bac4]">
                                                                {formatPriceWithCurrency(item.price)}
                                                            </span>
                                                        ) : null}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {info.note && (
                    <div className="rounded-lg border-2 border-gray-200 p-3 dark:border-gray-700 dark:bg-gray-900/30">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {getTranslation("orderContentPage.notes", language)}
                        </p>
                        <p className="mt-1 break-words text-sm font-bold text-gray-900 dark:text-gray-300">{info.note}</p>
                    </div>
                )}

                <div className="space-y-1.5 border-t border-gray-200 pt-3 text-sm dark:border-[#30363d]">
                    {Number(info.deliveryPrice) > 0 && (
                        <div className="flex justify-between">
                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation("orderContentPage.deliveryAmount", language)}
                            </span>
                            <span className="tabular-nums text-gray-600 dark:text-[#b1bac4]">
                                {formatPriceWithCurrency(info.deliveryPrice)}
                            </span>
                        </div>
                    )}
                    {promoCode && (
                        <div className="flex justify-between">
                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation("orderContentPage.promocode", language)}
                            </span>
                            <span className="text-gray-600 dark:text-[#b1bac4]">{promoCode}</span>
                        </div>
                    )}
                    {discountAmount !== null && (
                        <div className="flex justify-between">
                            <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation("orderContentPage.discountAmount", language)}
                            </span>
                            <span className="tabular-nums text-gray-600 dark:text-[#b1bac4]">
                                -{formatPriceWithCurrency(discountAmount)}
                            </span>
                        </div>
                    )}
                    <div className="flex justify-between pt-1 text-base font-semibold text-gray-900 dark:text-[#e6edf3]">
                        <span>{getTranslation("orderContentPage.total", language)}:</span>
                        <span className="tabular-nums">
                            {formatPriceWithCurrency(info.totalPrice || info.totalPriceBeforeDiscount || 0)}
                        </span>
                    </div>
                </div>

                <div className="flex justify-end border-t border-gray-100 pt-3 dark:border-[#21262d]">
                    <Button variant="primary" size="md" onClick={() => onOpenFull(order)}>
                        {getTranslation("orderPage.viewFullOrder", language)}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default BoardOrderDetailsModal;
